import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Metric,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  ExecutiveDashboard,
  ReportDefinition,
  ReportFormat,
  ReportRun,
  ReportType,
  ScheduledReportSweepResult,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, governanceApi } from "../../lib/api";
import {
  ActionGate,
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";
import {
  ALL_REPORT_FORMATS,
  REPORT_TYPES,
  RENDERER_REQUIRED,
  RunStatusBadge,
  formatDescription,
  formatLabel,
  reportTypeDescription,
  reportTypeLabel,
  triggerLabel,
} from "./reportLabels";

interface Member {
  id: string;
  email: string;
  displayName: string;
}

// Executive reporting (APP-004 R3 P6).
//
// A report here is evidence, not a picture: every run records the hash of what
// it measured, every artifact records the hash of what it produced, and every
// copy taken is recorded against the person who took it. Nothing on this screen
// is calculated in the browser, and readiness in particular is read from frozen
// snapshots rather than recomputed.
export function GovReports() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [sweep, setSweep] = useState<ScheduledReportSweepResult | null>(null);

  const definitions = useAsync(async () => (await governanceApi.reportDefinitions(orgId)).data, [
    orgId,
  ]);
  const runs = useAsync<ReportRun[]>(
    async () => (await governanceApi.reportRuns(orgId, { limit: 50 })).data.reportRuns,
    [orgId],
  );
  const executive = useAsync<ExecutiveDashboard>(
    async () => (await governanceApi.executiveDashboard(orgId)).data.executiveDashboard,
    [orgId],
  );
  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);

  const supported = definitions.data?.supportedFormats ?? [];
  const rows = definitions.data?.reportDefinitions ?? [];
  const memberName = (id: string) => {
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };
  const lastRunOf = (definitionId: string) =>
    (runs.data ?? [])
      .filter((run) => run.definitionId === definitionId)
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0] ?? null;

  const reload = () => {
    definitions.reload();
    runs.reload();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <p>
            Deterministic exports of the governance position. Running the same report over an
            unchanged position produces the same input hash, so two copies of a figure can always be
            reconciled.
          </p>
        </div>
        <div className="badge-row">
          <GovernedAction
            permission={GOVERNANCE_PERMISSIONS.auditsManage}
            label="Run due schedules"
            title="Run every schedule that is due"
            description="Runs each enabled definition whose next run time has passed."
            consequence="A due schedule runs once. Running the sweep again reports nothing due."
            confirmLabel="Run schedules"
            busyLabel="Running…"
            onConfirm={async () => {
              const res = await governanceApi.runScheduledReports(orgId);
              setSweep(res.data.result);
              return res;
            }}
            onDone={reload}
          />
          <CreateDefinition
            orgId={orgId}
            supported={supported}
            members={members.data ?? []}
            onDone={reload}
          />
        </div>
      </div>

      {sweep ? (
        <Card>
          <CardHeader title="Last schedule sweep" />
          <CardBody>
            <div className="metric-grid">
              <div className="metric-card">
                <Metric value={sweep.due} label="Schedules due" />
              </div>
              <div className="metric-card">
                <Metric value={sweep.executed} label="Ran successfully" />
              </div>
              <div className="metric-card">
                <Metric value={sweep.failed} label="Failed" />
              </div>
            </div>
            <p className="muted-note">A repeat sweep reports nothing due.</p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader title="Governance position" />
        <CardBody>
          <p className="muted-note">
            What these reports measure. Every figure links to the records behind it.
          </p>
          {executive.data ? (
            <>
              <div className="metric-grid" data-testid="reporting-headline">
                <div className="metric-card">
                  <Metric
                    value={`${Math.round(executive.data.headline.policyCoverageRatio * 100)}%`}
                    label="Verified requirement coverage"
                  />
                  <Link to="/studio/governance/frameworks">Frameworks</Link>
                </div>
                <div className="metric-card">
                  <Metric
                    value={`${Math.round(executive.data.headline.evidenceFreshnessRatio * 100)}%`}
                    label="Evidence freshness"
                  />
                  <Link to="/studio/governance/evidence">Evidence</Link>
                </div>
                <div className="metric-card">
                  <Metric
                    value={executive.data.headline.averageReadinessScore ?? "No frozen snapshot"}
                    label="Average readiness score"
                  />
                  <Link to="/studio/governance/audits">Audits</Link>
                </div>
                <div className="metric-card">
                  <Metric
                    value={executive.data.headline.openFindings}
                    label="Findings still open"
                  />
                  <Link to="/studio/governance/audits">Findings</Link>
                </div>
              </div>
              <p className="muted-note">
                Measured {formatDate(executive.data.generatedAt)}. Readiness comes from frozen audit
                snapshots. Reporting never recalculates it, and AI confidence never affects it.
              </p>
            </>
          ) : (
            <p className="muted-note">Loading the governance position…</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Export formats" />
        <CardBody>
          <p className="muted-note">What this deployment can actually produce right now.</p>
          {definitions.data ? (
            <ul className="plain-list" data-testid="supported-formats">
              {ALL_REPORT_FORMATS.map((format) => {
                const available = supported.includes(format);
                return (
                  <li key={format}>
                    <Badge tone={available ? "success" : "neutral"}>
                      {available ? "Available" : "Unavailable"}
                    </Badge>{" "}
                    <strong>{formatLabel(format)}</strong>{" "}
                    <span className="muted-note">
                      {available ? formatDescription(format) : RENDERER_REQUIRED}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            // Until the backend answers, no claim is made: telling a customer a
            // format needs a renderer this deployment may well have is a claim
            // about the deployment, not a loading state.
            <p className="muted-note">Asking the backend which formats it can render…</p>
          )}
        </CardBody>
      </Card>

      <GovPanel
        title="Report definitions"
        state={definitions}
        loadingLabel="Loading report definitions…"
        isEmpty={(data) => data.reportDefinitions.length === 0}
        emptyTitle="No report definitions"
        emptyDescription="Define the report you need, then run it by hand or on an interval."
      >
        {() => (
          <DataTable
            caption="Report definitions"
            columns={[
              {
                key: "name",
                header: "Report",
                render: (row: ReportDefinition) => (
                  <>
                    <strong>{row.name}</strong>
                    <div className="badge-row">
                      <Badge tone={row.enabled ? "success" : "neutral"}>
                        {row.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                      <Badge tone="info">{reportTypeLabel(row.reportType)}</Badge>
                    </div>
                    {row.description ? <div className="muted-note">{row.description}</div> : null}
                  </>
                ),
              },
              {
                key: "formats",
                header: "Formats",
                render: (row: ReportDefinition) =>
                  row.formats.map((format) => formatLabel(format)).join(", "),
              },
              {
                key: "schedule",
                header: "Schedule",
                render: (row: ReportDefinition) =>
                  row.scheduleIntervalDays === null ? (
                    <span className="muted-note">Run by hand</span>
                  ) : (
                    <>
                      Every {row.scheduleIntervalDays} days
                      <div className="muted-note">
                        Next {row.nextRunAt ? formatDate(row.nextRunAt) : "not scheduled"}
                      </div>
                    </>
                  ),
              },
              {
                key: "last",
                header: "Last run",
                render: (row: ReportDefinition) => {
                  const last = lastRunOf(row.id);
                  return last ? (
                    <>
                      <Link to={`/studio/governance/reports/runs/${last.id}`}>
                        {formatDate(last.startedAt)}
                      </Link>
                      <div className="badge-row">
                        <RunStatusBadge status={last.status} />
                      </div>
                    </>
                  ) : (
                    <span className="muted-note">Never run</span>
                  );
                },
              },
              {
                key: "recipients",
                header: "Recipients",
                render: (row: ReportDefinition) =>
                  row.recipients.length === 0 ? (
                    <span className="muted-note">Nobody notified</span>
                  ) : (
                    row.recipients.map(memberName).join(", ")
                  ),
              },
              {
                key: "actions",
                header: "",
                render: (row: ReportDefinition) => (
                  <div className="badge-row">
                    <GovernedAction
                      permission={GOVERNANCE_PERMISSIONS.auditsManage}
                      label="Run now"
                      title={`Run ${row.name}`}
                      description="Measures the governance position now and renders every format on this definition."
                      consequence="The run and its artifacts are kept permanently, including the hash of what was measured."
                      confirmLabel="Run report"
                      busyLabel="Running…"
                      onConfirm={() => governanceApi.runReportDefinition(orgId, row.id)}
                      onDone={reload}
                    />
                    <GovernedAction
                      permission={GOVERNANCE_PERMISSIONS.auditsManage}
                      label={row.enabled ? "Disable" : "Enable"}
                      title={row.enabled ? `Disable ${row.name}` : `Enable ${row.name}`}
                      description={
                        row.enabled
                          ? "Stops this definition running. Its schedule is cancelled and past runs are kept."
                          : "Allows this definition to run again. Its interval restarts from now."
                      }
                      confirmLabel={row.enabled ? "Disable" : "Enable"}
                      onConfirm={() =>
                        governanceApi.setReportDefinitionEnabled(orgId, row.id, !row.enabled)
                      }
                      onDone={reload}
                    />
                  </div>
                ),
              },
            ]}
            rows={rows}
          />
        )}
      </GovPanel>

      <GovPanel
        title="Run history"
        state={runs}
        loadingLabel="Loading run history…"
        isEmpty={(data) => data.length === 0}
        emptyTitle="Nothing has run yet"
        emptyDescription="Run a definition by hand, or wait for its schedule."
      >
        {(data) => (
          <DataTable
            caption="Report runs"
            columns={[
              {
                key: "started",
                header: "Run",
                render: (row: ReportRun) => (
                  <>
                    <Link to={`/studio/governance/reports/runs/${row.id}`}>
                      {formatDate(row.startedAt)}
                    </Link>
                    <div className="muted-note">{reportTypeLabel(row.reportType)}</div>
                  </>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (row: ReportRun) => (
                  <>
                    <RunStatusBadge status={row.status} />
                    {row.error ? <div className="muted-note">{row.error}</div> : null}
                  </>
                ),
              },
              { key: "trigger", header: "Trigger", render: (row: ReportRun) => triggerLabel(row.trigger) },
              { key: "rows", header: "Rows", render: (row: ReportRun) => row.rowCount },
              {
                key: "hash",
                header: "Input hash",
                render: (row: ReportRun) =>
                  row.inputsHash ? (
                    <code>{row.inputsHash.slice(0, 16)}…</code>
                  ) : (
                    <span className="muted-note">Nothing measured</span>
                  ),
              },
            ]}
            rows={data}
          />
        )}
      </GovPanel>
    </>
  );
}

function CreateDefinition({
  orgId,
  supported,
  members,
  onDone,
}: {
  orgId: string;
  supported: ReportFormat[];
  members: Member[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [definitionKey, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [reportType, setReportType] = useState<ReportType>("governance_dashboard");
  const [formats, setFormats] = useState<ReportFormat[]>(["csv"]);
  const [scheduled, setScheduled] = useState(false);
  const [intervalDays, setIntervalDays] = useState("30");
  const [recipients, setRecipients] = useState<string[]>([]);
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.auditsManage);

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  const interval = Number(intervalDays);
  const intervalValid = !scheduled || (Number.isInteger(interval) && interval >= 1 && interval <= 365);

  const submit = async () => {
    const ok = await mutation.run(() =>
      governanceApi.createReportDefinition(orgId, {
        definitionKey: definitionKey.trim(),
        name: name.trim(),
        description: description.trim(),
        reportType,
        formats,
        scheduleIntervalDays: scheduled ? interval : null,
        recipients,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setKey("");
    setName("");
    setDescription("");
    setFormats(["csv"]);
    setScheduled(false);
    setRecipients([]);
    onDone();
  };

  return (
    <>
      <ActionGate permission={GOVERNANCE_PERMISSIONS.auditsManage}>
        <Button variant="primary" onClick={() => setOpen(true)}>
          Create report definition
        </Button>
      </ActionGate>
      <Dialog
        open={open}
        title="Create a report definition"
        description="A definition says what to measure and in which formats. Running it produces a dated, hashed copy."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                !definitionKey.trim() ||
                !name.trim() ||
                formats.length === 0 ||
                !intervalValid ||
                mutation.busy
              }
              onClick={() => void submit()}
            >
              {mutation.busy ? "Creating…" : "Create definition"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Key" help="Stable identifier for this definition. It cannot be reused.">
          {(id) => (
            <TextInput
              id={id}
              value={definitionKey}
              placeholder="quarterly-governance"
              onChange={(e) => setKey(e.target.value)}
            />
          )}
        </Field>
        <Field label="Name">
          {(id) => (
            <TextInput
              id={id}
              value={name}
              placeholder="Quarterly governance pack"
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Description">
          {(id) => (
            <TextArea
              id={id}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field label="Report type" help={reportTypeDescription(reportType)}>
          {(id) => (
            <Select
              id={id}
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
            >
              {REPORT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {reportTypeLabel(type)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <fieldset className="ods-fieldset">
          <legend>Formats</legend>
          {ALL_REPORT_FORMATS.map((format) => {
            const available = supported.includes(format);
            return (
              <label key={format} className="ods-check">
                <input
                  type="checkbox"
                  checked={formats.includes(format)}
                  disabled={!available}
                  onChange={() => setFormats((current) => toggle(current, format))}
                />
                <span>
                  {formatLabel(format)}{" "}
                  <span className="muted-note">
                    {available ? formatDescription(format) : RENDERER_REQUIRED}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <fieldset className="ods-fieldset">
          <legend>Interval scheduling</legend>
          <label className="ods-check">
            <input
              type="checkbox"
              checked={scheduled}
              onChange={() => setScheduled((current) => !current)}
            />
            <span>Run this report on an interval</span>
          </label>
          {scheduled ? (
            <Field
              label="Interval in days"
              help="Interval scheduling only: the backend runs the report this many days after the last run. There is no calendar or cron alignment."
            >
              {(id) => (
                <TextInput
                  id={id}
                  type="number"
                  min={1}
                  max={365}
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(e.target.value)}
                />
              )}
            </Field>
          ) : null}
        </fieldset>

        <fieldset className="ods-fieldset">
          <legend>Recipients</legend>
          <p className="muted-note">Notified when a run completes. They still need the export permission to take a copy.</p>
          {members.map((member) => (
            <label key={member.id} className="ods-check">
              <input
                type="checkbox"
                checked={recipients.includes(member.id)}
                onChange={() => setRecipients((current) => toggle(current, member.id))}
              />
              <span>{member.displayName || member.email}</span>
            </label>
          ))}
        </fieldset>
      </Dialog>
    </>
  );
}
